import { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  adminDashboardOverview,
  adminDashboardTimeseries,
  adminDashboardUsage,
  adminDashboardBusinessHealth,
  adminListUsers,
  adminUpdateUser,
  adminDeleteUser,
  adminTotpRevoke,
} from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, Users, UserPlus, UserMinus, CreditCard, Sparkles,
  TrendingUp, TrendingDown, Activity, LineChart as LineIcon,
  Lock, Search, MoreHorizontal, RefreshCw,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, AreaChart, Area,
} from "recharts";

const ICON_MAP = {
  users: Users, "user-plus": UserPlus, "user-minus": UserMinus,
  "credit-card": CreditCard, sparkles: Sparkles, "trending-up": TrendingUp,
  "trending-down": TrendingDown, activity: Activity, "line-chart": LineIcon,
};

function formatValue(v, format) {
  if (v === null || v === undefined) return "—";
  if (format === "currency") return `$${Number(v).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  if (format === "percent") return `${Number(v).toFixed(1)}%`;
  return Number(v).toLocaleString("es-ES");
}

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" });
}

// ---------------------------------------------------------------------------
function OverviewCard({ card }) {
  const Icon = ICON_MAP[card.icon] || Activity;
  const v = card.variation_pct;
  const isUp = v !== null && v !== undefined && v > 0;
  const isDown = v !== null && v !== undefined && v < 0;
  const isNeutralNegative = ["churn", "canceled"].includes(card.key);
  return (
    <div className="app-card p-5 group relative" data-testid={`admin-card-${card.key}`} title={card.source || ""}>
      <div className="flex items-center justify-between mb-3">
        <div className="w-8 h-8 rounded-lg bg-foreground/[0.04] grid place-items-center">
          <Icon className="w-4 h-4 text-foreground/70" strokeWidth={2} />
        </div>
        {v !== null && v !== undefined ? (
          <div className={`text-[11px] font-semibold flex items-center gap-1 ${
            isUp ? (isNeutralNegative ? "text-rose-600" : "text-emerald-600") :
            isDown ? (isNeutralNegative ? "text-emerald-600" : "text-rose-600") :
            "text-foreground/50"
          }`}>
            {isUp ? <TrendingUp className="w-3 h-3" /> : isDown ? <TrendingDown className="w-3 h-3" /> : null}
            {v > 0 ? "+" : ""}{v}%
          </div>
        ) : null}
      </div>
      <div className="text-[11px] uppercase tracking-wider text-foreground/50 font-semibold mb-1">{card.label}</div>
      <div className="font-display text-2xl font-black leading-none tracking-tight">
        {formatValue(card.value, card.format)}
      </div>
      {card.source ? (
        <div className="text-[10px] text-foreground/40 mt-2 leading-tight truncate">
          {card.source}
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
function ChartCard({ title, children, testid }) {
  return (
    <div className="app-card p-5" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wider text-foreground/50 font-semibold mb-4">{title}</div>
      <div className="h-56 -mx-2">{children}</div>
    </div>
  );
}

function EvolutionSection() {
  const [range, setRange] = useState("30d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let m = true;
    setLoading(true);
    adminDashboardTimeseries(range)
      .then((d) => { if (m) setData(d); })
      .catch(() => toast.error("No se pudo cargar la evolución"))
      .finally(() => { if (m) setLoading(false); });
    return () => { m = false; };
  }, [range]);

  const merged = useMemo(() => {
    if (!data) return [];
    const map = new Map();
    for (const s of data.series.users) map.set(s.date, { date: s.date, users: s.value, subs: 0, revenue: 0 });
    for (const s of data.series.subscriptions) {
      const e = map.get(s.date) || { date: s.date, users: 0, subs: 0, revenue: 0 };
      e.subs = s.value;
      map.set(s.date, e);
    }
    for (const s of data.series.revenue) {
      const e = map.get(s.date) || { date: s.date, users: 0, subs: 0, revenue: 0 };
      e.revenue = s.value;
      map.set(s.date, e);
    }
    return Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date));
  }, [data]);

  const ranges = [
    { key: "7d", label: "7 días" },
    { key: "30d", label: "30 días" },
    { key: "90d", label: "90 días" },
    { key: "12m", label: "12 meses" },
  ];

  return (
    <section data-testid="admin-evolution-section" className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl font-black">Evolución del negocio</h2>
        <div className="flex bg-foreground/[0.04] rounded-xl p-1 gap-1">
          {ranges.map((r) => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              data-testid={`admin-range-${r.key}`}
              className={`px-3 py-1.5 text-[12px] font-medium rounded-lg transition-colors ${
                range === r.key ? "bg-white text-foreground shadow-sm" : "text-foreground/60 hover:text-foreground"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>
      {loading ? (
        <div className="app-card p-10 grid place-items-center h-56"><Loader2 className="w-5 h-5 animate-spin text-foreground/50" /></div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ChartCard title="Nuevos usuarios" testid="chart-users">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={merged} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="gUsers" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0A0A0A" stopOpacity={0.18} />
                    <stop offset="100%" stopColor="#0A0A0A" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "rgba(0,0,0,0.5)" }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 10, fill: "rgba(0,0,0,0.5)" }} tickLine={false} axisLine={false} width={30} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid rgba(0,0,0,0.08)", fontSize: 12 }} />
                <Area type="monotone" dataKey="users" stroke="#0A0A0A" strokeWidth={2} fill="url(#gUsers)" />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
          <ChartCard title="Nuevas suscripciones" testid="chart-subs">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={merged} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "rgba(0,0,0,0.5)" }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 10, fill: "rgba(0,0,0,0.5)" }} tickLine={false} axisLine={false} width={30} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid rgba(0,0,0,0.08)", fontSize: 12 }} />
                <Line type="monotone" dataKey="subs" stroke="#10B981" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
          <ChartCard title="Ingresos generados (USD)" testid="chart-revenue">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={merged} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "rgba(0,0,0,0.5)" }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 10, fill: "rgba(0,0,0,0.5)" }} tickLine={false} axisLine={false} width={30} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid rgba(0,0,0,0.08)", fontSize: 12 }} formatter={(v) => `$${v}`} />
                <Bar dataKey="revenue" fill="#F59E0B" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
          <ChartCard title="Crecimiento (usuarios acumulados)" testid="chart-growth">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={cumulative(merged, "users")} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "rgba(0,0,0,0.5)" }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 10, fill: "rgba(0,0,0,0.5)" }} tickLine={false} axisLine={false} width={30} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid rgba(0,0,0,0.08)", fontSize: 12 }} />
                <Line type="monotone" dataKey="cumulative" stroke="#6366F1" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      )}
    </section>
  );
}

function cumulative(rows, key) {
  let acc = 0;
  return rows.map((r) => {
    acc += r[key] || 0;
    return { date: r.date, cumulative: acc };
  });
}

// ---------------------------------------------------------------------------
function UsageSection() {
  const [data, setData] = useState(null);
  useEffect(() => {
    adminDashboardUsage().then(setData).catch(() => toast.error("No se pudo cargar el uso"));
  }, []);
  if (!data) return <div className="app-card p-10 grid place-items-center h-40"><Loader2 className="w-5 h-5 animate-spin text-foreground/50" /></div>;
  const t = data.totals;
  const tiles = [
    { label: "Cálculos de precios", value: t.pricing_checks, testid: "usage-pricing" },
    { label: "Ofertas generadas", value: t.offers, testid: "usage-offers" },
    { label: "Propuestas creadas", value: t.proposals, testid: "usage-proposals" },
    { label: "Análisis de clientes", value: t.client_analyses, testid: "usage-analyses" },
  ];
  return (
    <section data-testid="admin-usage-section" className="space-y-4">
      <h2 className="font-display text-xl font-black">Uso de la plataforma</h2>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {tiles.map((tt) => (
          <div key={tt.label} className="app-card p-5" data-testid={tt.testid}>
            <div className="text-[11px] uppercase tracking-wider text-foreground/50 font-semibold mb-2">{tt.label}</div>
            <div className="font-display text-2xl font-black tracking-tight">{formatValue(tt.value)}</div>
          </div>
        ))}
      </div>
      <ChartCard title="Funciones más utilizadas" testid="chart-top-functions">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data.top_functions} layout="vertical" margin={{ top: 8, right: 16, left: 40, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 10, fill: "rgba(0,0,0,0.5)" }} tickLine={false} axisLine={false} />
            <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: "rgba(0,0,0,0.7)" }} tickLine={false} axisLine={false} width={160} />
            <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid rgba(0,0,0,0.08)", fontSize: 12 }} />
            <Bar dataKey="count" fill="#0A0A0A" radius={[0, 6, 6, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
    </section>
  );
}

// ---------------------------------------------------------------------------
const STATUS_COLORS = {
  active: "bg-emerald-100 text-emerald-700",
  trialing: "bg-sky-100 text-sky-700",
  canceled: "bg-rose-100 text-rose-700",
  past_due: "bg-amber-100 text-amber-700",
  suspended: "bg-zinc-200 text-zinc-700",
  incomplete: "bg-zinc-100 text-zinc-600",
};

function UsersSection() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [plan, setPlan] = useState("");
  const [sort, setSort] = useState("-created_at");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await adminListUsers({ page, page_size: pageSize, search, status, plan, sort });
      setRows(d.users);
      setTotal(d.total);
    } catch {
      toast.error("No se pudieron cargar los usuarios");
    } finally { setLoading(false); }
  }, [page, pageSize, search, status, plan, sort]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const toggleSort = (field) => {
    setSort((s) => (s === field ? `-${field}` : field));
    setPage(1);
  };

  const onDelete = async (u) => {
    if (!window.confirm(`Eliminar definitivamente a ${u.email}? Esta acción borra su cuenta, sesiones y biblioteca.`)) return;
    try {
      await adminDeleteUser(u.user_id);
      toast.success("Usuario eliminado");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar");
    }
  };

  const onSuspend = async (u) => {
    try {
      await adminUpdateUser(u.user_id, { subscription_status: u.subscription_status === "suspended" ? "active" : "suspended" });
      toast.success(u.subscription_status === "suspended" ? "Usuario reactivado" : "Usuario suspendido");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo actualizar");
    }
  };

  return (
    <section data-testid="admin-users-section" className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-display text-xl font-black">Gestión de usuarios</h2>
        <div className="text-[12px] text-foreground/60">{total} usuario{total === 1 ? "" : "s"}</div>
      </div>
      <div className="app-card p-4">
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-3.5 h-3.5 text-foreground/40 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={search}
              onChange={(e) => { setPage(1); setSearch(e.target.value); }}
              placeholder="Buscar por email o nombre..."
              className="w-full pl-8 pr-3 py-2 text-[13px] rounded-lg border border-black/[0.08] bg-white focus:outline-none focus:border-foreground/40"
              data-testid="admin-users-search"
            />
          </div>
          <select
            value={status}
            onChange={(e) => { setPage(1); setStatus(e.target.value); }}
            className="text-[12px] px-3 py-2 rounded-lg border border-black/[0.08] bg-white"
            data-testid="admin-users-status-filter"
          >
            <option value="">Todos los estados</option>
            <option value="active">Activo</option>
            <option value="trialing">Trial</option>
            <option value="canceled">Cancelado</option>
            <option value="past_due">Pago fallido</option>
            <option value="suspended">Suspendido</option>
          </select>
          <select
            value={plan}
            onChange={(e) => { setPage(1); setPlan(e.target.value); }}
            className="text-[12px] px-3 py-2 rounded-lg border border-black/[0.08] bg-white"
            data-testid="admin-users-plan-filter"
          >
            <option value="">Todos los planes</option>
            <option value="monthly">Mensual</option>
            <option value="yearly">Anual</option>
            <option value="admin">Admin</option>
          </select>
          <button
            onClick={load}
            className="p-2 rounded-lg border border-black/[0.08] bg-white hover:bg-foreground/[0.03]"
            data-testid="admin-users-refresh"
            title="Refrescar"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-foreground/50 font-semibold border-b border-black/[0.06]">
                <th className="py-2 px-2 cursor-pointer" onClick={() => toggleSort("name")}>Usuario</th>
                <th className="py-2 px-2 cursor-pointer" onClick={() => toggleSort("email")}>Email</th>
                <th className="py-2 px-2">Plan</th>
                <th className="py-2 px-2">Estado</th>
                <th className="py-2 px-2 cursor-pointer" onClick={() => toggleSort("created_at")}>Registro</th>
                <th className="py-2 px-2">Últ. acceso</th>
                <th className="py-2 px-2 text-right">Uso</th>
                <th className="py-2 px-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !loading ? (
                <tr><td colSpan={8} className="py-8 text-center text-foreground/50">Sin usuarios</td></tr>
              ) : rows.map((u) => (
                <tr key={u.user_id} data-testid={`user-row-${u.user_id}`} className="border-b border-black/[0.04] hover:bg-foreground/[0.02] transition-colors">
                  <td className="py-3 px-2">
                    <div className="flex items-center gap-2.5">
                      {u.picture ? (
                        <img src={u.picture} alt="" className="w-7 h-7 rounded-full object-cover" />
                      ) : (
                        <div className="w-7 h-7 rounded-full bg-foreground/[0.08] grid place-items-center text-[11px] font-semibold">
                          {(u.name || u.email || "?").slice(0, 1).toUpperCase()}
                        </div>
                      )}
                      <div className="font-medium truncate max-w-[160px]">{u.name || "—"}</div>
                    </div>
                  </td>
                  <td className="py-3 px-2 text-foreground/70 truncate max-w-[220px]">{u.email}</td>
                  <td className="py-3 px-2">
                    <span className="text-[11px] font-medium text-foreground/70">{u.plan || "—"}</span>
                  </td>
                  <td className="py-3 px-2">
                    <span className={`text-[10px] px-2 py-1 rounded-full font-semibold ${STATUS_COLORS[u.subscription_status] || "bg-zinc-100 text-zinc-600"}`}>
                      {u.subscription_status || "—"}
                    </span>
                  </td>
                  <td className="py-3 px-2 text-foreground/70">{formatDate(u.created_at)}</td>
                  <td className="py-3 px-2 text-foreground/70">{formatDate(u.last_login_at)}</td>
                  <td className="py-3 px-2 text-right font-mono">{u.calc_count || 0}</td>
                  <td className="py-3 px-2 text-right">
                    <div className="inline-flex gap-1">
                      <button onClick={() => setEditing(u)} className="text-[11px] px-2 py-1 rounded-md hover:bg-foreground/[0.05]" data-testid={`user-view-${u.user_id}`}>Ver</button>
                      <button onClick={() => onSuspend(u)} className="text-[11px] px-2 py-1 rounded-md hover:bg-foreground/[0.05]" data-testid={`user-suspend-${u.user_id}`}>
                        {u.subscription_status === "suspended" ? "Reactivar" : "Suspender"}
                      </button>
                      <button onClick={() => onDelete(u)} className="text-[11px] px-2 py-1 rounded-md text-rose-600 hover:bg-rose-50" data-testid={`user-delete-${u.user_id}`}>Eliminar</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between mt-4 pt-3 border-t border-black/[0.06]">
          <div className="text-[11px] text-foreground/55">
            Página {page} de {totalPages}
          </div>
          <div className="flex gap-1">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="text-[12px] px-3 py-1.5 rounded-lg border border-black/[0.08] bg-white disabled:opacity-40"
              data-testid="admin-users-prev"
            >
              Anterior
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="text-[12px] px-3 py-1.5 rounded-lg border border-black/[0.08] bg-white disabled:opacity-40"
              data-testid="admin-users-next"
            >
              Siguiente
            </button>
          </div>
        </div>
      </div>

      {editing ? <UserDetailModal user={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} /> : null}
    </section>
  );
}

function UserDetailModal({ user, onClose, onSaved }) {
  const [status, setStatus] = useState(user.subscription_status || "");
  const [plan, setPlan] = useState(user.plan || "");
  const [isAdmin, setIsAdmin] = useState(!!user.is_admin);
  const [saving, setSaving] = useState(false);

  const onSave = async () => {
    setSaving(true);
    try {
      await adminUpdateUser(user.user_id, { subscription_status: status, plan, is_admin: isAdmin });
      toast.success("Cambios guardados");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 grid place-items-center px-6" onClick={onClose} data-testid="user-detail-modal">
      <div className="bg-background rounded-2xl w-full max-w-lg p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 mb-5">
          {user.picture ? <img src={user.picture} alt="" className="w-12 h-12 rounded-full" /> : (
            <div className="w-12 h-12 rounded-full bg-foreground/[0.08] grid place-items-center font-semibold">
              {(user.name || user.email || "?").slice(0, 1).toUpperCase()}
            </div>
          )}
          <div>
            <div className="font-display text-lg font-black leading-tight">{user.name || "—"}</div>
            <div className="text-[12px] text-foreground/60">{user.email}</div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-5 text-[12px]">
          <Field label="Registro" value={formatDate(user.created_at)} />
          <Field label="Último acceso" value={formatDate(user.last_login_at)} />
          <Field label="Cálculos" value={user.calc_count || 0} />
          <Field label="Cancel al final" value={user.cancel_at_period_end ? "Sí" : "No"} />
        </div>

        <div className="space-y-3 mb-5">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-foreground/50 font-semibold mb-1 block">Estado</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="w-full px-3 py-2 text-[13px] rounded-lg border border-black/[0.08] bg-white" data-testid="edit-user-status">
              <option value="active">Activo</option>
              <option value="trialing">Trial</option>
              <option value="canceled">Cancelado</option>
              <option value="past_due">Pago fallido</option>
              <option value="suspended">Suspendido</option>
              <option value="incomplete">Incompleto</option>
            </select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-foreground/50 font-semibold mb-1 block">Plan</label>
            <select value={plan || ""} onChange={(e) => setPlan(e.target.value)} className="w-full px-3 py-2 text-[13px] rounded-lg border border-black/[0.08] bg-white" data-testid="edit-user-plan">
              <option value="monthly">Mensual</option>
              <option value="yearly">Anual</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <label className="flex items-center gap-2 text-[13px] cursor-pointer">
            <input type="checkbox" checked={isAdmin} onChange={(e) => setIsAdmin(e.target.checked)} data-testid="edit-user-is-admin" />
            <span>Es administrador</span>
          </label>
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t border-black/[0.06]">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-[13px] border border-black/[0.08] bg-white">Cancelar</button>
          <button onClick={onSave} disabled={saving} className="px-4 py-2 rounded-lg text-[13px] bg-foreground text-white disabled:opacity-60" data-testid="edit-user-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-foreground/50 font-semibold">{label}</div>
      <div className="font-medium">{value}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function BusinessHealthSection() {
  const [data, setData] = useState(null);
  useEffect(() => { adminDashboardBusinessHealth().then(setData).catch(() => {}); }, []);
  if (!data) return null;
  const items = [
    { label: "Renovaciones hoy", value: data.renewals_today, testid: "health-renewals-today" },
    { label: "Próximas renovaciones (7d)", value: data.upcoming_renewals, testid: "health-upcoming" },
    { label: "Pagos fallidos", value: data.failed_payments, testid: "health-failed", warn: data.failed_payments > 0 },
    { label: "Solicitudes de reembolso", value: data.refund_requests, testid: "health-refunds" },
    { label: "Solicitudes de cancelación", value: data.cancel_requests, testid: "health-cancels", warn: data.cancel_requests > 0 },
  ];
  return (
    <section data-testid="admin-health-section" className="space-y-4">
      <h2 className="font-display text-xl font-black">Salud del negocio</h2>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {items.map((i) => (
          <div key={i.label} className="app-card p-4" data-testid={i.testid}>
            <div className="text-[11px] uppercase tracking-wider text-foreground/50 font-semibold mb-2">{i.label}</div>
            <div className={`font-display text-2xl font-black tracking-tight ${i.warn ? "text-rose-600" : ""}`}>{formatValue(i.value)}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ===========================================================================
export default function AdminDashboard() {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminDashboardOverview()
      .then((d) => setOverview(d))
      .catch((err) => {
        const detail = err?.response?.data?.detail;
        if (detail?.error === "totp_challenge_required") navigate("/admin/verify", { replace: true });
        else toast.error(typeof detail === "string" ? detail : "No se pudo cargar el resumen");
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  const onLockAdmin = async () => {
    try {
      await adminTotpRevoke();
      toast.success("Sesión admin bloqueada");
      navigate("/admin/verify", { replace: true });
    } catch { toast.error("No se pudo bloquear"); }
  };

  if (loading) return <div className="min-h-screen grid place-items-center bg-background"><Loader2 className="w-6 h-6 animate-spin text-foreground/60" /></div>;

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard-page">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 space-y-10">
        <header className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <Link to="/dashboard" className="text-[12px] text-foreground/55 hover:text-foreground transition-colors mb-2 inline-block">
              ← Salir del panel
            </Link>
            <h1 className="font-display text-4xl font-black leading-tight tracking-tight">Panel de administración</h1>
            <p className="text-[13px] text-foreground/60 mt-1">Estado del negocio en tiempo real · Actualizado {overview ? new Date(overview.generated_at).toLocaleTimeString("es-ES") : ""}</p>
          </div>
          <button
            onClick={onLockAdmin}
            className="flex items-center gap-2 px-3.5 py-2 rounded-xl border border-black/[0.08] bg-white text-[12px] font-medium hover:bg-foreground/[0.03]"
            data-testid="admin-lock-btn"
          >
            <Lock className="w-3.5 h-3.5" /> Bloquear sesión
          </button>
        </header>

        {overview ? (
          <section data-testid="admin-overview-section" className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
              {overview.cards.map((c) => <OverviewCard key={c.key} card={c} />)}
            </div>
          </section>
        ) : null}

        <EvolutionSection />
        <UsageSection />
        <UsersSection />
        <BusinessHealthSection />

        <div className="pt-8 pb-4 text-[11px] text-foreground/40 text-center">
          Strato Admin · Solo visible para administradores con 2FA activa
        </div>
      </div>
    </div>
  );
}
